import React, { useState, useEffect } from 'react';
import {
    ArrowLeft,
    Mail,
    CheckCircle,
    AlertCircle,
    Loader2,
    ToggleLeft,
    ToggleRight,
    Eye,
    EyeOff,
    RefreshCw,
    Plus,
    Edit3,
    Save,
    X,
    KeyRound,
    ShieldCheck,
    Server,
    Lock,
    Unlock,
    AtSign,
    Globe,
    User,
    Activity,
    Database
} from 'lucide-react';
import { apiFetch } from '../api';
import AnimatedBackground from './Background';
import { useWebSocket } from '../websocket/WebSocketContext';
import { ensureSocketConnection } from '../websocket/socketUtils';

// Modal component for license, confirmation, and config details
const Modal = ({ isOpen, onClose, children, title }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-900/95 border border-purple-500/40 rounded-2xl shadow-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-95 animate-in fade-in-0 zoom-in-95">
                <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors">
                    <X className="w-6 h-6" />
                </button>
                {title && (
                    <h3 className="text-2xl font-bold mb-6 text-center text-white">{title}</h3>
                )}
                {children}
            </div>
        </div>
    );
};

export default function EmailConfig({ onBack, setError, API_BASE_URL, loginId, deliveryMode }) {

    const { client } = useWebSocket();

    const [configs, setConfigs] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setErrorLocal] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [showFormPassword, setShowFormPassword] = useState(false);
    const [showDetailsPassword, setShowDetailsPassword] = useState(false);

    // Form state for new/edit email configuration
    const [formData, setFormData] = useState({
        email_name: '',
        email_host: '',
        email_port: 587,
        email_username: '',
        email_password: '',
        email_protocol: 'smtp',
        tls_enable: true,
        auth_enable: true,
        notification_email: '',
        email_sent_form: ''
    });

    // State for modals and update flow
    const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [configToUpdate, setConfigToUpdate] = useState(null);
    const [licenseNo, setLicenseNo] = useState('');
    const [licenseError, setLicenseError] = useState('');

    // Queue management state
    const [selectedQueueType, setSelectedQueueType] = useState('0');
    const [currentQueueType, setCurrentQueueType] = useState(null);
    const [isLoadingQueue, setIsLoadingQueue] = useState(false);
    const [isUpdatingQueue, setIsUpdatingQueue] = useState(false);
    const [queueSuccess, setQueueSuccess] = useState('');
    const [queueError, setQueueError] = useState('');

    // App Name status management state
    const [appNameStatuses, setAppNameStatuses] = useState({});
    const [isLoadingAppStatuses, setIsLoadingAppStatuses] = useState(false);
    const [isUpdatingAppStatus, setIsUpdatingAppStatus] = useState(false);
    const [updatingAppName, setUpdatingAppName] = useState(null);

    // Queue type options
    const queueTypes = [
        { value: '0', label: 'Local Queue', icon: <Server className="w-4 h-4" />, color: 'blue' },
        { value: '1', label: 'Kafka Queue', icon: <Activity className="w-4 h-4" />, color: 'green' },
        { value: '2', label: 'Redis Queue', icon: <Database className="w-4 h-4" />, color: 'red' }
    ];

    // Clear messages after timeout
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess('');
                setErrorLocal('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    useEffect(() => {
        if (queueSuccess || queueError) {
            const timer = setTimeout(() => {
                setQueueSuccess('');
                setQueueError('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [queueSuccess, queueError]);

    const fetchAppNameStatuses = async () => {
        setIsLoadingAppStatuses(true);
        try {
            if (deliveryMode === 'socket') {
                try { await ensureSocketConnection(client); } catch (e) {
                    console.error(e); setErrorLocal('Socket connection failed.'); setIsLoadingAppStatuses(false); return;
                }

                const topic = `/topic/get/all/email/appName/status/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;
                        if (data.STATUS === 'SUCCESS' && data.DATA) {
                            setAppNameStatuses(data.DATA);
                        }
                    } catch (e) { console.error(e); } finally {
                        subscription.unsubscribe();
                        setIsLoadingAppStatuses(false);
                    }
                });

                client.publish({
                    destination: '/app/get/all/email/appName/status',
                    body: JSON.stringify({})
                });
            } else {
                // API
                const response = await apiFetch('/get/all/email/appName/status', { method: 'GET' });
                if (response.ok) {
                    const data = await response.json();
                    if (data.STATUS === 'SUCCESS' && data.DATA) {
                        setAppNameStatuses(data.DATA);
                    }
                }
                setIsLoadingAppStatuses(false);
            }
        } catch (err) {
            console.error("Error fetching app statuses:", err);
            setQueueError('Failed to load statuses.');
            setIsLoadingAppStatuses(false);
        }
    };

    // --- REFACTORED: Toggle App Name Status ---
    const toggleAppNameStatus = async (appName, currentStatus) => {
        setIsUpdatingAppStatus(true);
        setUpdatingAppName(appName);
        setQueueSuccess('');
        setQueueError('');

        const newStatus = currentStatus ? 'false' : 'true';

        try {
            if (deliveryMode === 'socket') {
                try { await ensureSocketConnection(client); } catch (e) {
                    setQueueError('Socket connection failed.'); setIsUpdatingAppStatus(false); setUpdatingAppName(null); return;
                }

                const topic = `/topic/active-or-de-active/email/appName/queue/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const data = JSON.parse(msg.body);
                        if (data.STATUS === 'SUCCESS') {
                            const statusText = newStatus === 'true' ? 'activated' : 'deactivated';
                            setQueueSuccess(`App "${appName}" ${statusText} successfully!`);
                            fetchAppNameStatuses();
                        } else {
                            setQueueError(`Failed: ${data.MESSAGE}`);
                        }
                    } catch (e) { console.error(e); } finally {
                        subscription.unsubscribe();
                        setIsUpdatingAppStatus(false);
                        setUpdatingAppName(null);
                    }
                });

                const payload = { appName: appName, active: newStatus };
                client.publish({
                    destination: '/app/active-or-de-active/email/appName/queue',
                    body: JSON.stringify(payload)
                });

            } else {
                // API
                const response = await apiFetch(`/active-or-de-active/email/appName/queue?appName=${appName}&active=${newStatus}`, {
                    method: 'PUT'
                });
                const data = await response.json();

                if (response.ok && data.STATUS === 'SUCCESS') {
                    const statusText = newStatus === 'true' ? 'activated' : 'deactivated';
                    setQueueSuccess(`App "${appName}" ${statusText} successfully!`);
                    await fetchAppNameStatuses();
                } else {
                    setQueueError(`Failed: ${data.MESSAGE || 'Unknown error'}`);
                }
                setIsUpdatingAppStatus(false);
                setUpdatingAppName(null);
            }
        } catch (err) {
            console.error("Error updating status:", err);
            setQueueError('Network error.');
            setIsUpdatingAppStatus(false);
            setUpdatingAppName(null);
        }
    };

    // --- REFACTORED: Fetch Current Queue Type ---
    const fetchCurrentQueueType = async () => {
        setIsLoadingQueue(true);
        try {
            if (deliveryMode === 'socket') {
                try { await ensureSocketConnection(client); } catch (e) { setIsLoadingQueue(false); return; }

                const topic = `/topic/running/queue/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;
                        if (data.STATUS === 'SUCCESS') {
                            setCurrentQueueType(data.data.toString());
                        }
                    } catch (e) { console.error(e); } finally {
                        subscription.unsubscribe();
                        setIsLoadingQueue(false);
                    }
                });

                // Sending type=1 as in your API call
                client.publish({
                    destination: '/app/running/queue',
                    body: JSON.stringify({ type: '1' })
                });

            } else {
                // API
                const response = await apiFetch('/running/queue?type=1', { method: 'GET' });
                if (response.ok) {
                    const data = await response.json();
                    if (data.STATUS === 'SUCCESS') {
                        setCurrentQueueType(data.data.toString());
                    }
                }
                setIsLoadingQueue(false);
            }
        } catch (err) {
            console.error("Error fetching queue type:", err);
            setIsLoadingQueue(false);
        }
    };

    // --- REFACTORED: Update Queue Type ---
    const updateQueueType = async () => {
        setIsUpdatingQueue(true);
        setQueueSuccess('');
        setQueueError('');

        try {
            if (deliveryMode === 'socket') {
                try { await ensureSocketConnection(client); } catch (e) {
                    setQueueError('Socket connection failed.'); setIsUpdatingQueue(false); return;
                }

                const topic = `/topic/active-or-de-active/email/queue/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const data = JSON.parse(msg.body);
                        if (data.STATUS === 'SUCCESS') {
                            const queueLabel = queueTypes.find(q => q.value === selectedQueueType)?.label;
                            setQueueSuccess(`Queue type switched to ${queueLabel}!`);
                            setCurrentQueueType(selectedQueueType);
                        } else {
                            setQueueError(`Failed: ${data.MESSAGE}`);
                        }
                    } catch (e) { console.error(e); } finally {
                        subscription.unsubscribe();
                        setIsUpdatingQueue(false);
                    }
                });

                client.publish({
                    destination: '/app/active-or-de-active/email/queue',
                    body: JSON.stringify({ type: selectedQueueType })
                });

            } else {
                // API
                const response = await apiFetch(`/active-or-de-active/email/queue?type=${selectedQueueType}`, {
                    method: 'POST'
                });
                const data = await response.json();

                if (response.ok && data.STATUS === 'SUCCESS') {
                    const queueLabel = queueTypes.find(q => q.value === selectedQueueType)?.label;
                    setQueueSuccess(`Queue type switched to ${queueLabel} successfully!`);
                    setCurrentQueueType(selectedQueueType);
                } else {
                    setQueueError(`Failed: ${data.MESSAGE || 'Unknown error'}`);
                }
                setIsUpdatingQueue(false);
            }
        } catch (err) {
            console.error("Error updating queue:", err);
            setQueueError('Network error.');
            setIsUpdatingQueue(false);
        }
    };

    // --- REFACTORED: Fetch Configs ---
    const fetchConfigs = async () => {
        setIsLoading(true);
        try {
            if (deliveryMode === 'socket') {
                try { await ensureSocketConnection(client); } catch (e) {
                    setErrorLocal('Socket connection failed.'); setIsLoading(false); return;
                }

                const topic = `/topic/email/config/getAll/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                            setConfigs(data.Response);
                            if (data.Response.length > 0 && !selectedConfig) {
                                setSelectedConfig(data.Response[0]);
                            } else if (selectedConfig) {
                                const updated = data.Response.find(c => c._id === selectedConfig._id);
                                if (updated) setSelectedConfig(updated);
                            }
                        } else {
                            setErrorLocal('Failed to load configs via socket.');
                        }
                    } catch (e) { console.error(e); } finally {
                        subscription.unsubscribe();
                        setIsLoading(false);
                    }
                });

                client.publish({
                    destination: '/app/email/config/getAll',
                    body: JSON.stringify({})
                });

            } else {
                // API
                const response = await apiFetch("/email/config/getAll");
                if (!response.ok) throw new Error(`HTTP error! status: ${response.STATUS}`);
                const data = await response.json();

                if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                    setConfigs(data.Response);
                    if (data.Response.length > 0 && !selectedConfig) {
                        setSelectedConfig(data.Response[0]);
                    } else if (selectedConfig) {
                        const updated = data.Response.find(c => c._id === selectedConfig._id);
                        if (updated) setSelectedConfig(updated);
                    }
                } else {
                    setErrorLocal('Failed to load configs.');
                }
                setIsLoading(false);
            }
        } catch (err) {
            console.error("Error fetching configs:", err);
            setErrorLocal('Network error.');
            setIsLoading(false);
        }
    };

    // Handle toggle click
    const handleToggleClick = (config) => {
        setConfigToUpdate(config);
        setIsLicenseModalOpen(true);
    };

    // Handle license submit
    const handleLicenseSubmit = () => {
        if (!licenseNo.trim()) {
            setLicenseError('License number cannot be empty.');
            return;
        }
        setLicenseError('');
        setIsLicenseModalOpen(false);
        setIsConfirmModalOpen(true);
    };

    // --- REFACTORED: Execute Status Update (Toggle Active/Inactive) ---
    const executeStatusUpdate = async () => {
        if (!configToUpdate || !licenseNo) return;

        setIsConfirmModalOpen(false);
        setIsUpdating(true);
        const newStatusFlag = configToUpdate.STATUS !== 'ACTIVE';
        const newStatus = newStatusFlag ? 'ACTIVE' : 'INACTIVE';

        try {
            if (deliveryMode === 'socket') {
                try { await ensureSocketConnection(client); } catch (e) {
                    setErrorLocal('Socket connection failed.'); setIsUpdating(false); return;
                }

                const topic = `/topic/email/config/active/deActive/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const data = JSON.parse(msg.body);
                        if (data.STATUS === 'SUCCESS') {
                            setSuccess(`Configuration ${newStatus.toLowerCase()}d successfully!`);
                            fetchCurrentQueueType();
                            fetchAppNameStatuses();

                            const updatedConfigs = configs.map(c =>
                                c._id === configToUpdate._id ? { ...c, status: newStatus } : c
                            );
                            setConfigs(updatedConfigs);
                            if (selectedConfig && selectedConfig._id === configToUpdate._id) {
                                setSelectedConfig(prev => ({ ...prev, status: newStatus }));
                            }
                        } else {
                            setErrorLocal(`Failed: ${data.message}`);
                        }
                    } catch (e) { console.error(e); } finally {
                        subscription.unsubscribe();
                        setIsUpdating(false);
                        setConfigToUpdate(null);
                        setLicenseNo('');
                    }
                });

                // Passing params in body for socket
                const payload = {
                    id: configToUpdate._id,
                    active: newStatusFlag,
                    licenseNumber: licenseNo
                };
                client.publish({
                    destination: '/app/email/config/active/deActive',
                    body: JSON.stringify(payload)
                });

            } else {
                // API
                const response = await apiFetch(
                    `/email/config/active/deActive?id=${configToUpdate._id}&active=${newStatusFlag}&licenseNumber=${licenseNo}`,
                    { method: 'PUT' }
                );

                if (!response.ok) throw new Error(`HTTP error! status: ${response.STATUS}`);
                const data = await response.json();

                if (data.STATUS === 'SUCCESS') {
                    setSuccess(`Configuration ${newStatus.toLowerCase()}d successfully!`);
                    await fetchCurrentQueueType();
                    await fetchAppNameStatuses();
                    // Execute status update
                    const executeStatusUpdate = async () => {
                        if (!configToUpdate || !licenseNo) return;

                        setIsConfirmModalOpen(false);
                        setIsUpdating(true);
                        const newStatusFlag = configToUpdate.STATUS !== 'ACTIVE';

                        try {
                            const response = await apiFetch(
                                `/email/config/active/deActive?id=${configToUpdate._id}&active=${newStatusFlag}&licenseNumber=${licenseNo}`,
                                {
                                    method: 'PUT',
                                }
                            );

                            if (!response.ok) {
                                throw new Error(`HTTP error! status: ${response.STATUS}`);
                            }

                            const data = await response.json();

                            if (data.STATUS === 'SUCCESS') {
                                const newStatus = newStatusFlag ? 'ACTIVE' : 'INACTIVE';
                                setSuccess(`Email configuration ${newStatus.toLowerCase()}d successfully!`);

                                await fetchCurrentQueueType();
                                await fetchAppNameStatuses();

                                const updatedConfigs = configs.map(c =>
                                    c._id === configToUpdate._id ? { ...c, status: newStatus } : c
                                );
                                setConfigs(updatedConfigs);

                                if (selectedConfig && selectedConfig._id === configToUpdate._id) {
                                    setSelectedConfig(prev => ({ ...prev, status: newStatus }));
                                }
                            } else {
                                setErrorLocal(`Failed to update: ${data.message || 'Unknown error'}`);
                            }
                        } catch (err) {
                            console.error("Error toggling config status:", err);
                            setErrorLocal('Failed to update email configuration status. Network error.');
                        } finally {
                            setIsUpdating(false);
                            setConfigToUpdate(null);
                            setLicenseNo('');
                        }
                    };
                    const updatedConfigs = configs.map(c =>
                        c._id === configToUpdate._id ? { ...c, status: newStatus } : c
                    );
                    setConfigs(updatedConfigs);
                    if (selectedConfig && selectedConfig._id === configToUpdate._id) {
                        setSelectedConfig(prev => ({ ...prev, status: newStatus }));
                    }
                } else {
                    setErrorLocal(`Failed to update: ${data.message || 'Unknown error'}`);
                }
                setIsUpdating(false);
                setConfigToUpdate(null);
                setLicenseNo('');
            }
        } catch (err) {
            console.error("Error toggling status:", err);
            setErrorLocal('Network error.');
            setIsUpdating(false);
            setConfigToUpdate(null);
            setLicenseNo('');
        }
    };


    // Handle form input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            email_name: '',
            email_host: '',
            email_port: 587,
            email_username: '',
            email_password: '',
            email_protocol: 'smtp',
            tls_enable: true,
            auth_enable: true,
            notification_email: '', 
            email_sent_form: ''
        });
        setIsEditing(false);
        setShowFormPassword(false);
    };

    // Load config into form for editing
    const loadConfigForEdit = (config) => {
        setFormData({
            email_name: config.email_name || '',
            email_host: config.email_host || '',
            email_port: config.email_port || 587,
            email_username: config.email_username || '',
            email_password: config.email_password || '',
            email_protocol: config.email_protocol || 'smtp',
            tls_enable: config.tls_enable !== undefined ? config.tls_enable : true,
            auth_enable: config.auth_enable !== undefined ? config.auth_enable : true,
            notification_email: config.notification_email || '',
            email_sent_form: config.email_sent_form || '',
        });
        setIsEditing(true);
    };

    // --- REFACTORED: Save / Update Config ---
    const handleSaveConfig = async () => {
        setIsSaving(true);
        setSuccess('');
        setErrorLocal('');

        // Validation
        const requiredFields = ['email_name', 'email_host', 'email_username', 'email_password', 'email_protocol',
            'notification_email', 'email_sent_form'];
        for (const field of requiredFields) {
            if (!formData[field]?.trim()) {
                setErrorLocal(`${field.replace(/_/g, ' ').toUpperCase()} is required.`);
                setIsSaving(false);
                return;
            }
        }
        if (!formData.email_port || isNaN(formData.email_port)) {
            setErrorLocal('Valid port is required.');
            setIsSaving(false);
            return;
        }

        try {
            if (deliveryMode === 'socket') {
                try { await ensureSocketConnection(client); } catch (e) {
                    setErrorLocal('Socket connection failed.'); setIsSaving(false); return;
                }

                // Determine topic and destination
                const isUpdate = isEditing && selectedConfig;
                const endpoint = isUpdate ? 'update' : 'save';
                const topic = `/topic/email/config/${endpoint}/${loginId}`;
                const destination = `/app/email/config/${endpoint}`;

                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const data = JSON.parse(msg.body);
                        if (data.STATUS === 'SUCCESS') {
                            setSuccess(`Email Configuration ${isUpdate ? 'updated' : 'saved'} successfully!`);
                            resetForm();
                            fetchConfigs();
                            fetchAppNameStatuses();
                            fetchCurrentQueueType();
                        } else {
                            setErrorLocal(`Failed: ${data.message}`);
                        }
                    } catch (e) { console.error(e); } finally {
                        subscription.unsubscribe();
                        setIsSaving(false);
                    }
                });

                // Prepare payload
                const payload = { ...formData };
                if (isUpdate) payload.id = selectedConfig._id; // Attach ID for update

                client.publish({
                    destination: destination,
                    body: JSON.stringify(payload)
                });

            } else {
                // API
                let response;
                if (isEditing && selectedConfig) {
                    response = await apiFetch(`/email/config/update?id=${selectedConfig._id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(formData),
                    });
                } else {
                    response = await apiFetch('/email/config/save', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(formData),
                    });
                }

                if (!response.ok) throw new Error(`HTTP error! status: ${response.STATUS}`);
                const data = await response.json();

                if (data.STATUS === 'SUCCESS') {
                    setSuccess(`Email Configuration ${isEditing ? 'updated' : 'saved'} successfully!`);
                    resetForm();
                    await fetchConfigs();
                    await fetchAppNameStatuses();
                    await fetchCurrentQueueType();
                } else {
                    setErrorLocal(`Failed: ${data.message || 'Unknown error'}`);
                }
                setIsSaving(false);
            }
        } catch (err) {
            console.error("Error saving config:", err);
            setErrorLocal('Network error.');
            setIsSaving(false);
        }
    };

    const handleRefresh = async () => {
        fetchConfigs();
        fetchCurrentQueueType();
        fetchAppNameStatuses();
    }

    // Initial load
    useEffect(() => {
        fetchConfigs();
        fetchCurrentQueueType();
        fetchAppNameStatuses();
    }, [API_BASE_URL]);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = new Date(timestamp);
            return date.toLocaleString();
        } catch {
            return timestamp;
        }
    };

    const getCurrentQueueLabel = () => {
        return queueTypes.find(q => q.value === currentQueueType)?.label || 'Unknown';
    };

    const getCurrentQueueColor = () => {
        return queueTypes.find(q => q.value === currentQueueType)?.color || 'gray';
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 to-[#649EE8] font-inter text-white">
            {/* Background Elements */}
            {/* <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-1/3 -left-1/3 w-2/3 h-2/3 bg-gradient-to-br from-blue-600/10 to-transparent rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-1/3 -right-1/3 w-2/3 h-2/3 bg-gradient-to-tl from-purple-600/10 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
            </div> */}

            {/* Render the animated background */}
            <AnimatedBackground />

            <div className="relative z-10 p-6 md:p-10 max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-10">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={onBack}
                            className="flex items-center space-x-2 px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            <span>Back to Dashboard</span>
                        </button>
                        <div className="flex items-center space-x-4">
                            <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg">
                                <Mail className="w-7 h-7" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold tracking-tight">Email Configuration</h1>
                                <p className="text-white/70 text-sm">Manage your email service settings</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={fetchCurrentQueueType}
                            disabled={isLoadingQueue}
                            className="flex items-center space-x-2 px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/40 rounded-xl text-orange-200 transition-all duration-300 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoadingQueue ? 'animate-spin' : ''}`} />
                            <span>Queue Status</span>
                        </button>
                        <button
                            onClick={fetchAppNameStatuses}
                            disabled={isLoadingAppStatuses}
                            className="flex items-center space-x-2 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 rounded-xl text-cyan-200 transition-all duration-300 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoadingAppStatuses ? 'animate-spin' : ''}`} />
                            <span>App Status</span>
                        </button>
                        <button
                            onClick={handleRefresh}
                            disabled={isLoading}
                            className="flex items-center space-x-2 px-5 py-3 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-600/40 rounded-xl text-purple-200 transition-all duration-300 disabled:opacity-50 shadow-md hover:shadow-lg"
                        >
                            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                {/* Global Messages */}
                {(success || error || queueSuccess || queueError) && (
                    <div className="mb-6 space-y-3">
                        {(success || error) && (
                            <div className={`p-4 rounded-xl flex items-center space-x-3 border backdrop-blur-sm ${success
                                ? 'bg-green-500/20 border-green-500/40 text-green-200'
                                : 'bg-red-500/20 border-red-500/40 text-red-200'
                                }`}>
                                {success ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                                <span className="font-medium">{success || error}</span>
                            </div>
                        )}
                        {(queueSuccess || queueError) && (
                            <div className={`p-4 rounded-xl flex items-center space-x-3 border backdrop-blur-sm ${queueSuccess
                                ? 'bg-green-500/20 border-green-500/40 text-green-200'
                                : 'bg-red-500/20 border-red-500/40 text-red-200'
                                }`}>
                                {queueSuccess ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                                <span className="font-medium">{queueSuccess || queueError}</span>
                            </div>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Sidebar - Email Config List */}
                    <div className="lg:col-span-1">
                        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-xl">
                            <div className="flex items-center space-x-3 mb-6">
                                <Mail className="w-6 h-6 text-blue-400" />
                                <h3 className="text-xl font-semibold">Email Configurations</h3>
                                <span className="px-3 py-1 bg-blue-600/20 text-blue-300 text-sm rounded-full">
                                    {configs.length}
                                </span>
                            </div>

                            {isLoading ? (
                                <div className="flex items-center justify-center py-10">
                                    <Loader2 className="w-7 h-7 animate-spin text-blue-400" />
                                    <span className="ml-3 text-white/70">Loading...</span>
                                </div>
                            ) : configs.length > 0 ? (
                                <div className="space-y-4 max-h-[600px] overflow-y-auto custom-scrollbar pr-2">
                                    {configs.map((config) => {
                                        // Logic to determine the specific app's status
                                        const appNameStatus = appNameStatuses[config.email_name];
                                        const isAppActive = appNameStatus === true || appNameStatus === 'true';
                                        const isCurrentlyUpdating = updatingAppName === config._id && isUpdatingAppStatus;

                                        return (
                                            <div
                                                key={config._id}
                                                onClick={() => {
                                                    setSelectedConfig(config);
                                                    setIsDetailsModalOpen(true);
                                                }}
                                                className={`p-5 rounded-xl border cursor-pointer transition-all duration-300 hover:scale-[1.02] shadow-sm hover:shadow-md ${selectedConfig?._id === config._id
                                                    ? 'bg-blue-600/20 border-blue-500/50'
                                                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="font-semibold text-base">{config.email_name}</h4>
                                                    <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm ${config.STATUS === 'ACTIVE'
                                                        ? 'bg-green-600/20 text-green-300'
                                                        : 'bg-red-600/20 text-red-300'
                                                        }`}>
                                                        <div className={`w-2.5 h-2.5 rounded-full ${config.STATUS === 'ACTIVE' ? 'bg-green-400' : 'bg-red-400'
                                                            }`}></div>
                                                        <span>{config.STATUS}</span>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-white/60 mb-3">
                                                    {config.email_username}
                                                </p>

                                                {/* --- Copied & Integrated App Name Status Toggle --- */}
                                                <div className="flex items-center justify-between bg-white/5 rounded-lg p-2 mb-3">
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-xs text-white/60">App Status:</span>
                                                        <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${isAppActive
                                                            ? 'bg-green-500/20 text-green-300'
                                                            : appNameStatus === false || appNameStatus === 'false'
                                                                ? 'bg-red-500/20 text-red-300'
                                                                : 'bg-gray-500/20 text-gray-300'
                                                            }`}>
                                                            <div className={`w-2 h-2 rounded-full ${isAppActive ? 'bg-green-400' :
                                                                appNameStatus === false || appNameStatus === 'false' ? 'bg-red-400' : 'bg-gray-400'
                                                                }`}></div>
                                                            <span>
                                                                {isLoadingAppStatuses ? 'Loading...' :
                                                                    isAppActive ? 'Active' :
                                                                        appNameStatus === false || appNameStatus === 'false' ? 'Inactive' : 'Unknown'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent modal from opening
                                                            toggleAppNameStatus(config.email_name, isAppActive);
                                                        }}
                                                        disabled={isCurrentlyUpdating || isLoadingAppStatuses || appNameStatus === undefined}
                                                        className={`flex items-center space-x-1 px-2 py-1 rounded-lg transition-all duration-300 text-xs ${isAppActive
                                                            ? 'bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30'
                                                            : 'bg-green-500/20 border border-green-500/40 text-green-200 hover:bg-green-500/30'
                                                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                    >
                                                        {isCurrentlyUpdating ? (
                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                        ) : isAppActive ? (
                                                            <ToggleRight className="w-4 h-4" />
                                                        ) : (
                                                            <ToggleLeft className="w-4 h-4" />
                                                        )}
                                                        <span className="font-medium">
                                                            {isCurrentlyUpdating ? 'Updating...' : isAppActive ? 'Deactivate' : 'Activate'}
                                                        </span>
                                                    </button>
                                                </div>
                                                {/* --- End of Integrated Block --- */}

                                                <p className="text-sm text-white/60">
                                                    Created: {formatTimestamp(config.creation_time_str)}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-10 text-white/60">
                                    <Mail className="w-14 h-14 mx-auto mb-3 opacity-50" />
                                    <p>No email configurations found</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Content Area - Form */}
                    <div className="lg:col-span-2">

                        {/* Queue Management Section */}
                        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                                    <Activity className="w-5 h-5 text-cyan-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-white">Queue Management</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Current Queue Status */}
                                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                    <div className="flex items-center space-x-3 mb-3">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${currentQueueType !== null ? `bg-${getCurrentQueueColor()}-500/20` : 'bg-gray-500/20'
                                            }`}>
                                            {isLoadingQueue ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                queueTypes.find(q => q.value === currentQueueType)?.icon || <Server className="w-4 h-4" />
                                            )}
                                        </div>
                                        <div>
                                            <label className="text-white/60 text-xs uppercase tracking-wider">Current Queue</label>
                                            <p className={`font-medium text-sm ${currentQueueType !== null ? `text-${getCurrentQueueColor()}-300` : 'text-gray-300'
                                                }`}>
                                                {isLoadingQueue ? 'Loading...' : getCurrentQueueLabel()}
                                            </p>
                                        </div>
                                    </div>
                                    {currentQueueType !== null && (
                                        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs bg-${getCurrentQueueColor()}-500/20 text-${getCurrentQueueColor()}-300`}>
                                            <div className={`w-2 h-2 rounded-full bg-${getCurrentQueueColor()}-400 animate-pulse`}></div>
                                            <span>Active</span>
                                        </div>
                                    )}
                                </div>

                                {/* Queue Type Selection */}
                                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider mb-3 block">Switch Queue Type</label>
                                    <select
                                        value={selectedQueueType}
                                        onChange={(e) => setSelectedQueueType(e.target.value)}
                                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300 mb-4"
                                        disabled={isUpdatingQueue}
                                    >
                                        {queueTypes.map((type) => (
                                            <option key={type.value} value={type.value} className="bg-slate-800 text-white">
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={updateQueueType}
                                        disabled={isUpdatingQueue || selectedQueueType === currentQueueType}
                                        className="w-full py-2 rounded-lg font-medium text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                                    >
                                        {isUpdatingQueue ? (
                                            <div className="flex items-center justify-center space-x-2">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span>Updating...</span>
                                            </div>
                                        ) : (
                                            <span>Switch Queue Type</span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                        {/* email config form */}
                        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-xl">
                            <div className="flex items-center justify-between mb-8">
                                <div className="flex items-center space-x-4">
                                    <div className="w-12 h-12 bg-orange-600/20 rounded-xl flex items-center justify-center">
                                        <Plus className="w-6 h-6 text-orange-400" />
                                    </div>
                                    <h3 className="text-2xl font-semibold">
                                        {isEditing ? 'Edit Email Configuration' : 'Create Email Configuration'}
                                    </h3>
                                </div>
                                {isEditing && (
                                    <button
                                        onClick={resetForm}
                                        className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl transition-all duration-300 shadow-sm"
                                    >
                                        <X className="w-5 h-5" />
                                        <span>Cancel Edit</span>
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Email Name */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <AtSign className="w-4 h-4 inline mr-2" />
                                        Email Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.email_name}
                                        onChange={(e) => handleInputChange('email_name', e.target.value)}
                                        placeholder="Enter email name..."
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* Host */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <Globe className="w-4 h-4 inline mr-2" />
                                        Host *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.email_host}
                                        onChange={(e) => handleInputChange('email_host', e.target.value)}
                                        placeholder="smtp.example.com"
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* Port */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <Server className="w-4 h-4 inline mr-2" />
                                        Port *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.email_port}
                                        onChange={(e) => handleInputChange('email_port', parseInt(e.target.value) || 587)}
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* Username */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <User className="w-4 h-4 inline mr-2" />
                                        Username *
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email_username}
                                        onChange={(e) => handleInputChange('email_username', e.target.value)}
                                        placeholder="your-email@example.com"
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* Sent From Email */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <User className="w-4 h-4 inline mr-2" />
                                        Sent From Email *
                                    </label>
                                    <input
                                        type="Sent From Email.."
                                        value={formData.email_sent_form}
                                        onChange={(e) => handleInputChange('email_sent_form', e.target.value)}
                                        placeholder="your-email@example.com"
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* Password */}
                                <div className="md:col-span-2">
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <Lock className="w-4 h-4 inline mr-2" />
                                        Password *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showFormPassword ? "text" : "password"}
                                            value={formData.email_password}
                                            onChange={(e) => handleInputChange('email_password', e.target.value)}
                                            placeholder="Enter password..."
                                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                            disabled={isSaving}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowFormPassword(!showFormPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                                        >
                                            {showFormPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                {/* Manager Email */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <User className="w-4 h-4 inline mr-2" />
                                        Manager Email *
                                    </label>
                                    <input
                                        type="Manager Email.."
                                        value={formData.notification_email}
                                        onChange={(e) => handleInputChange('notification_email', e.target.value)}
                                        placeholder="your-email@example.com"
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* Protocol */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <ShieldCheck className="w-4 h-4 inline mr-2" />
                                        Protocol *
                                    </label>
                                    <select
                                        value={formData.email_protocol}
                                        onChange={(e) => handleInputChange('email_protocol', e.target.value)}
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    >
                                        <option value="smtp">SMTP</option>
                                        <option value="smtps">SMTPS</option>
                                    </select>
                                </div>

                                {/* TLS and Auth */}
                                <div className="flex space-x-4">
                                    <div className="flex-1 flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-xl shadow-sm">
                                        <span className="text-white/90 text-sm font-medium">TLS Enable</span>
                                        <button
                                            onClick={() => handleInputChange('tls_enable', !formData.tls_enable)}
                                            disabled={isSaving}
                                            className={`flex items-center space-x-2 px-3 py-1 rounded-lg transition-all duration-300 disabled:opacity-50 ${formData.tls_enable
                                                ? 'bg-green-600/20 text-green-300'
                                                : 'bg-red-600/20 text-red-300'
                                                }`}
                                        >
                                            {formData.tls_enable ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                                            <span>{formData.tls_enable ? 'ON' : 'OFF'}</span>
                                        </button>
                                    </div>
                                    <div className="flex-1 flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-xl shadow-sm">
                                        <span className="text-white/90 text-sm font-medium">Auth Enable</span>
                                        <button
                                            onClick={() => handleInputChange('auth_enable', !formData.auth_enable)}
                                            disabled={isSaving}
                                            className={`flex items-center space-x-2 px-3 py-1 rounded-lg transition-all duration-300 disabled:opacity-50 ${formData.auth_enable
                                                ? 'bg-green-600/20 text-green-300'
                                                : 'bg-red-600/20 text-red-300'
                                                }`}
                                        >
                                            {formData.auth_enable ? <ShieldCheck className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                                            <span>{formData.auth_enable ? 'ON' : 'OFF'}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Save Button */}
                            <button
                                onClick={handleSaveConfig}
                                disabled={isSaving}
                                className="w-full mt-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl disabled:cursor-not-allowed transform hover:scale-[1.02] transition-all duration-300 relative overflow-hidden bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 hover:from-orange-700 hover:via-red-700 hover:to-pink-700 disabled:opacity-50 group"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                {isSaving ? (
                                    <div className="flex items-center justify-center space-x-3 text-white relative z-10">
                                        <Loader2 className="w-6 h-6 animate-spin" />
                                        <span>{isEditing ? 'Updating...' : 'Saving...'}</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center space-x-3 text-white relative z-10">
                                        <Save className="w-6 h-6" />
                                        <span>{isEditing ? 'Update Configuration' : 'Save Configuration'}</span>
                                    </div>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Details Modal */}
                {selectedConfig && (
                    <Modal
                        isOpen={isDetailsModalOpen}
                        onClose={() => setIsDetailsModalOpen(false)}
                        title={`${selectedConfig.email_name} Details`}
                    >
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                        <label className="text-white/60 text-xs uppercase tracking-wider">Email Name</label>
                                        <p className="font-medium mt-1">{selectedConfig.email_name}</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                        <label className="text-white/60 text-xs uppercase tracking-wider">Host</label>
                                        <p className="font-medium mt-1">{selectedConfig.email_host}</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                        <label className="text-white/60 text-xs uppercase tracking-wider">Port</label>
                                        <p className="font-medium mt-1">{selectedConfig.email_port}</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                        <label className="text-white/60 text-xs uppercase tracking-wider">Username</label>
                                        <p className="font-medium mt-1">{selectedConfig.email_username}</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                        <label className="text-white/60 text-xs uppercase tracking-wider">Email Sent From</label>
                                        <p className="font-medium mt-1">{selectedConfig.email_sent_form}</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                        <label className="text-white/60 text-xs uppercase tracking-wider">Manager Email</label>
                                        <p className="font-medium mt-1">{selectedConfig.notification_email}</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                        <label className="text-white/60 text-xs uppercase tracking-wider">Protocol</label>
                                        <p className="font-medium mt-1 uppercase">{selectedConfig.email_protocol}</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                        <label className="text-white/60 text-xs uppercase tracking-wider">TLS Enable</label>
                                        <p className="font-medium mt-1">{selectedConfig.tls_enable ? 'Enabled' : 'Disabled'}</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                        <label className="text-white/60 text-xs uppercase tracking-wider">Auth Enable</label>
                                        <p className="font-medium mt-1">{selectedConfig.auth_enable ? 'Enabled' : 'Disabled'}</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                        <label className="text-white/60 text-xs uppercase tracking-wider">Status</label>
                                        <div className="flex items-center space-x-2 mt-1">
                                            <div className={`w-3 h-3 rounded-full ${selectedConfig.STATUS === 'ACTIVE' ? 'bg-green-400' : 'bg-red-400'
                                                }`}></div>
                                            <span className={`font-medium ${selectedConfig.STATUS === 'ACTIVE' ? 'text-green-300' : 'text-red-300'
                                                }`}>
                                                {selectedConfig.STATUS}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <label className="text-white/90 text-sm font-medium">Password</label>
                                    <button
                                        onClick={() => setShowDetailsPassword(!showDetailsPassword)}
                                        className="flex items-center space-x-2 px-3 py-1 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg text-white/80 transition-all duration-300"
                                    >
                                        {showDetailsPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        <span className="text-sm">{showDetailsPassword ? 'Hide' : 'Show'}</span>
                                    </button>
                                </div>
                                <div className="bg-slate-900/50 border border-white/10 rounded-xl p-4">
                                    <p className="font-mono text-sm text-white/80">
                                        {showDetailsPassword ? selectedConfig.email_password : '••••••••••••••••'}
                                    </p>
                                </div>
                            </div>

                            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                <label className="text-white/60 text-xs uppercase tracking-wider">Created</label>
                                <p className="font-medium mt-1">{formatTimestamp(selectedConfig.creation_time_str)}</p>
                            </div>

                            <div className="flex items-center justify-between mt-6">
                                <div className="flex items-center space-x-3">
                                    <button
                                        onClick={() => {
                                            if (selectedConfig.STATUS === 'ACTIVE') {
                                                loadConfigForEdit(selectedConfig);
                                                setIsDetailsModalOpen(false);
                                            }
                                        }}
                                        disabled={selectedConfig.STATUS !== 'ACTIVE'}
                                        className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 shadow-sm ${selectedConfig.STATUS === 'ACTIVE'
                                            ? 'bg-blue-600/20 hover:bg-blue-600/30 border border-blue-600/40 text-blue-200'
                                            : 'bg-gray-600/20 border border-gray-600/40 text-gray-400 cursor-not-allowed'
                                            }`}
                                    >
                                        <Edit3 className="w-5 h-5" />
                                        <span>Edit</span>
                                    </button>
                                    <button
                                        onClick={() => handleToggleClick(selectedConfig)}
                                        disabled={isUpdating}
                                        className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 shadow-sm ${selectedConfig.STATUS === 'ACTIVE'
                                            ? 'bg-green-600/20 border border-green-600/40 text-green-200 hover:bg-green-600/30'
                                            : 'bg-red-600/20 border border-red-600/40 text-red-200 hover:bg-red-600/30'
                                            } disabled:opacity-50`}
                                    >
                                        {isUpdating && configToUpdate?._id === selectedConfig._id ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : selectedConfig.STATUS === 'ACTIVE' ? (
                                            <ToggleRight className="w-5 h-5" />
                                        ) : (
                                            <ToggleLeft className="w-5 h-5" />
                                        )}
                                        <span className="font-medium">
                                            {selectedConfig.STATUS === 'ACTIVE' ? 'Active' : 'Inactive'}
                                        </span>
                                    </button>
                                </div>
                                {selectedConfig.STATUS !== 'ACTIVE' && (
                                    <div className="flex items-center space-x-2 text-yellow-400">
                                        <AlertCircle className="w-5 h-5" />
                                        <span className="text-sm">Editing is only available when configuration is active.</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Modal>
                )}

                {/* License Modal */}
                <Modal isOpen={isLicenseModalOpen} onClose={() => setIsLicenseModalOpen(false)} title="License Key Required">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-purple-600/20 border border-purple-600/40 mb-6">
                            <KeyRound className="h-8 w-8 text-purple-300" />
                        </div>
                        <p className="text-white/70 mb-6">Please enter your license number to proceed with this action.</p>
                        <input
                            type="text"
                            value={licenseNo}
                            onChange={(e) => {
                                setLicenseNo(e.target.value);
                                if (licenseError) setLicenseError('');
                            }}
                            placeholder="Enter license number..."
                            className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all duration-300 shadow-sm"
                        />
                        {licenseError && <p className="text-red-400 text-sm mt-2">{licenseError}</p>}
                        <div className="mt-8 flex justify-end space-x-4">
                            <button onClick={() => setIsLicenseModalOpen(false)} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 transition-colors shadow-sm">Cancel</button>
                            <button onClick={handleLicenseSubmit} className="px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 font-semibold transition-colors shadow-sm">Submit</button>
                        </div>
                    </div>
                </Modal>

                {/* Confirmation Modal */}
                <Modal isOpen={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} title="Confirm Action">
                    {configToUpdate && (
                        <div className="text-center">
                            <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full border mb-6 ${configToUpdate.STATUS === 'ACTIVE' ? 'bg-red-600/20 border-red-600/40' : 'bg-green-600/20 border-green-600/40'
                                }`}>
                                <ShieldCheck className={`h-8 w-8 ${configToUpdate.STATUS === 'ACTIVE' ? 'text-red-300' : 'text-green-300'
                                    }`} />
                            </div>
                            <p className="text-white/70 mb-6">
                                Are you sure you want to <span className="font-bold">{configToUpdate.STATUS === 'ACTIVE' ? 'deactivate' : 'activate'}</span> the email configuration for <span className="font-bold text-purple-300">{configToUpdate.email_name}</span>?
                            </p>
                            <div className="mt-8 flex justify-end space-x-4">
                                <button onClick={() => setIsConfirmModalOpen(false)} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 transition-colors shadow-sm">Cancel</button>
                                <button
                                    onClick={executeStatusUpdate}
                                    className={`px-6 py-3 rounded-xl font-semibold transition-colors shadow-sm ${configToUpdate.STATUS === 'ACTIVE' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                                        }`}
                                >
                                    {isUpdating ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : `Yes, ${configToUpdate.STATUS === 'ACTIVE' ? 'Deactivate' : 'Activate'}`}
                                </button>
                            </div>
                        </div>
                    )}
                </Modal>

                <style jsx>{`
                    .custom-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: rgba(100, 116, 139, 0.4) rgba(51, 65, 85, 0.4);
                    }
                    .custom-scrollbar::-webkit-scrollbar {
                        width: 8px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-track {
                        background-color: rgba(51, 65, 85, 0.4);
                        border-radius: 0.5rem;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                        background-color: rgba(100, 116, 139, 0.4);
                        border-radius: 0.5rem;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                        background-color: rgba(100, 116, 139, 0.6);
                    }
                `}</style>
            </div>
        </div>
    );
}